import { LISTINGS_PAGE_SIZE, MARKETPLACE_IMAGE_FALLBACK, SEC_OFFERING_TYPE_LABELS } from '../config/constants';
import {
  getPropertyListingMode,
  normalizeCountryIso,
  getDisplayCountry,
  propertyMatchesCountry,
  inferCountryFromLocation,
  resolveListingCountryClient,
  formatUsd,
  propertyManagerFromRecord,
} from '../data/countries';
import apiClient from './api';
import { normalizeRaisingMode } from './raisingMode';

const filterPropertiesByListingMode = (list, mode) => {
  const arr = Array.isArray(list) ? list : [];
  if (mode === "rent") return arr.filter((p) => getPropertyListingMode(p) === "rent");
  return arr.filter((p) => getPropertyListingMode(p) === "sale");
};

/** Keep demo properties on the site; add/override with anything from the API. */
const mergeApiWithMockProperties = (apiList, mockList) => {
  const byId = new Map();
  for (const p of Array.isArray(apiList) ? apiList : []) {
    if (p?.id == null || p.id === "") continue;
    byId.set(String(p.id), p);
  }
  const api = [...byId.values()];
  const mock = Array.isArray(mockList) ? mockList : [];
  return [...api, ...mock.filter((m) => m?.id != null && !byId.has(String(m.id)))];
};

/** One card per property id (catalog/favorites merges can repeat the same listing). */
const dedupePropertiesById = (list) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter((p) => {
    const id = p?.id == null ? "" : String(p.id);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const findPropertyById = (properties, id) =>
  properties.find((p) => p.id === id || String(p.id) === String(id));

/** All photo URLs for a live property (gallery, images[], or legacy image). */
const getPropertyPhotoUrls = (p) => {
  if (!p) return [];
  const seen = new Set();
  const urls = [];
  const add = (raw) => {
    const s = String(raw || "").trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    urls.push(s);
  };
  if (Array.isArray(p.gallery)) p.gallery.forEach(add);
  if (Array.isArray(p.images)) p.images.forEach(add);
  if (!urls.length) add(p.image);
  return urls;
};

const getPropertyCardImage = (p) => getPropertyPhotoUrls(p)[0] || "";

const propertyMatchesBrowseCategory = (property, category) => {
  if (!category || category === "Any Category") return true;
  if (category === "Land") return Boolean(property.landBanking);
  return property.type === category;
};

const propertyMatchesBrowsePriceRange = (property, minVal, maxVal) => {
  const min = Number(String(minVal ?? "").replace(/,/g, ""));
  const max = Number(String(maxVal ?? "").replace(/,/g, ""));
  const minBound = Number.isFinite(min) && min > 0 ? min : 0;
  const maxBound = Number.isFinite(max) && max > 0 ? max : Infinity;
  const isRent = getPropertyListingMode(property) === "rent";
  const amount = isRent ? (Number(property.monthlyRent) || 0) : (Number(property.price) || 0);
  return amount >= minBound && amount <= maxBound;
};

const propertyMatchesBrowseSearch = (property, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const title = (property.title || "").toLowerCase();
  const location = (property.location || "").toLowerCase();
  const resolvedCountry = resolveListingCountryClient(property.country, property.location);
  const countryCode = normalizeCountryIso(resolvedCountry).toLowerCase();
  const countryName = getDisplayCountry(resolvedCountry).name.toLowerCase();
  const inferredFromLoc = inferCountryFromLocation(property.location);
  const inferredName = inferredFromLoc ? getDisplayCountry(inferredFromLoc).name.toLowerCase() : "";
  const locParts = location.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
  const city = locParts[0] || "";
  const stateRegion = locParts.slice(1).join(" ");
  const zip = String(property.zip || property.postalCode || property.zipCode || "").toLowerCase();
  const haystack = [title, location, city, stateRegion, countryCode, countryName, inferredName, zip].join(" ");
  return haystack.includes(q) || q.split(/\s+/).every((token) => token.length > 1 && haystack.includes(token));
};

/** Parse API/Firestore timestamps for display on property cards. */
const parsePropertyTimestamp = (val) => {
  if (val == null || val === "") return null;
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
  if (typeof val === "string") {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof val === "object" && typeof val.toDate === "function") {
    const d = val.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof val === "object") {
    const sec = val.seconds ?? val._seconds;
    if (typeof sec === "number") return new Date(sec * 1000);
  }
  return null;
};

/** When the listing went live on the main Properties page. */
const getPropertyListedAt = (p) =>
  parsePropertyTimestamp(p?.publishedAt)
  || parsePropertyTimestamp(p?.createdAt)
  || parsePropertyTimestamp(p?.listedAt)
  || null;

const formatPropertyDateTime = (date) => {
  if (!date) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

const formatListedLocalYear = (date) => String(date.getFullYear());

const formatListedLocalMonth = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const formatListedLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Filter listings by when they went live on site (published/created/listed). */
const propertyMatchesListedDateFilter = (property, { mode, month, date, year } = {}) => {
  if (!mode || mode === "all") return true;
  const listed = getPropertyListedAt(property);
  if (!listed) return false;

  if (mode === "year") {
    if (!year) return true;
    return formatListedLocalYear(listed) === String(year);
  }
  if (mode === "month") {
    if (!month) return true;
    return formatListedLocalMonth(listed) === String(month);
  }
  if (mode === "date") {
    if (!date) return true;
    return formatListedLocalDate(listed) === String(date);
  }
  return true;
};

const isPropertyFavorited = (favorites, propertyId) =>
  (favorites || []).some((f) => String(f) === String(propertyId));

const isPropertyComparing = (compareList, propertyId) =>
  (compareList || []).some((p) => String(p.id) === String(propertyId));

const getUserDisplayName = (user) => {
  if (!user) return "Guest";
  const full = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  if (full) return full;
  if (user.email) return user.email.split("@")[0];
  return "Investor";
};

const getUserInitials = (user) => {
  if (!user) return null;
  const full = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  if (full) return full.split(/\s+/).map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  if (user.email) return user.email.slice(0, 2).toUpperCase();
  return null;
};

const upgradeGoogleAvatarUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw || !raw.includes("googleusercontent.com")) return raw || null;
  if (/=s\d+/.test(raw)) return raw.replace(/=s\d+(-[a-z]+)?/i, "=s512$1");
  if (/\/s\d+(-[a-z]+)?$/i.test(raw)) return raw.replace(/\/s\d+(-[a-z]+)?$/i, "/s512$1");
  return raw;
};

const getUserAvatarUrl = (user) => {
  const url = String(user?.avatar || user?.profilePhotoUrl || "").trim();
  return upgradeGoogleAvatarUrl(url) || null;
};

const normalizeFavoriteIds = (ids) =>
  [...new Set((ids || []).map((id) => String(id)))];

const paginateArray = (items, page, pageSize = LISTINGS_PAGE_SIZE) => {
  const list = Array.isArray(items) ? items : [];
  const total = list.length;
  if (total === 0) {
    return {
      items: [],
      page: 1,
      pageSize,
      total: 0,
      totalPages: 1,
      hasPrev: false,
      hasNext: false,
      startIndex: 0,
      endIndex: 0
    };
  }
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const start = (safePage - 1) * pageSize;
  const slice = list.slice(start, start + pageSize);
  return {
    items: slice,
    page: safePage,
    pageSize,
    total,
    totalPages,
    hasPrev: safePage > 1,
    hasNext: safePage < totalPages,
    startIndex: start + 1,
    endIndex: start + slice.length
  };
};

const scrollPageToTop = () => {
  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch (_) {
    window.scrollTo(0, 0);
  }
};

/** Run after React has painted the new route (for in-page anchors). */
const runAfterPagePaint = (fn) => {
  if (!fn) return;
  requestAnimationFrame(() => requestAnimationFrame(fn));
};

const scrollToListingsAnchor = () => {
  try {
    const el = document.querySelector("[data-listings-anchor]");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (_) { /* ignore */ }
};

const scrollToCoownFaq = () => {
  try {
    const el = document.getElementById("coown-faq");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (_) { /* ignore */ }
};

const resolveMarketplaceProperty = (catalog, listing) =>
  listing ? findPropertyById(catalog, listing.propertyId) : null;

const getMarketListingPresentation = (catalog, listing) => {
  const p = resolveMarketplaceProperty(catalog, listing);
  return {
    property: p,
    title: p?.title || listing?.propertyTitle || "Property listing",
    location: p?.location || listing?.propertyLocation || "",
    image: getPropertyPhotoUrls(p)[0] || listing?.propertyImage || MARKETPLACE_IMAGE_FALLBACK,
    type: p?.type || listing?.propertyType || "Residential",
    country: (p?.country || listing?.propertyCountry || "").toUpperCase(),
    galleryCount: getPropertyPhotoUrls(p).length || (listing?.propertyImage ? 1 : 0)
  };
};

const marketListingIsSecondarySale = (catalog, listing) => {
  const p = resolveMarketplaceProperty(catalog, listing);
  if (p) return getPropertyListingMode(p) === "sale";
  return listing?.listingMode !== "rent";
};

const filterMarketListingsForView = (listings, catalog, { country, typeFilter }) =>
  (listings || []).filter((listing) => {
    if (listing.status && listing.status !== 'active') return false;
    if (Number(listing.shares) <= 0) return false;
    if (!marketListingIsSecondarySale(catalog, listing)) return false;
    const pres = getMarketListingPresentation(catalog, listing);
    const property = resolveMarketplaceProperty(catalog, listing);
    if (property && !isPropertyFullyFunded(property)) return false;
    if (!propertyMatchesCountry(pres.country, country)) return false;
    if (typeFilter !== "All" && pres.type !== typeFilter) return false;
    return true;
  });

const getEffectiveMinInvestment = (property) => {
  const p = property || {};
  const price = Number(p.price) || 0;
  const shares = Number(p.shares) || 0;
  if (normalizeRaisingMode(p.raisingMode) === "co_owners" && price > 0 && shares > 0) {
    return Math.max(1, Math.round((price / shares) * 100) / 100);
  }
  const fromApi = Number(p.minInvestment);
  if (fromApi > 0) return fromApi;
  if (price > 0 && shares > 0) return Math.max(1, Math.round((price / shares) * 100) / 100);
  return 500;
};

const offeringFormFromRecord = (r) => {
  const pm = propertyManagerFromRecord(r);
  return {
    fundingDeadline: r?.fundingDeadline || "",
    projectedTimeline: r?.projectedTimeline || "",
    riskRating: r?.riskRating || "",
    secOfferingType: r?.secOfferingType || "",
    propertyManagerName: pm.name,
    propertyManagerLicenseReference: pm.licenseReference,
    developerName: r?.developer?.name || "",
    developerTrackRecord: r?.developer?.trackRecord || ""
  };
};

const buildOfferingApiBody = (form) => ({
  fundingDeadline: String(form.fundingDeadline || "").trim(),
  projectedTimeline: String(form.projectedTimeline || "").trim(),
  riskRating: String(form.riskRating || "").trim(),
  secOfferingType: String(form.secOfferingType || "").trim(),
  propertyManagerName: String(form.propertyManagerName || "").trim(),
  propertyManagerLicenseReference: String(
    form.propertyManagerLicenseReference || ""
  ).trim(),
  developerName: String(form.developerName || "").trim(),
  developerTrackRecord: String(form.developerTrackRecord || "").trim()
});

const buildOfferingPropertyPatch = (form) => {
  const body = buildOfferingApiBody(form);
  return {
    fundingDeadline: body.fundingDeadline,
    projectedTimeline: body.projectedTimeline,
    riskRating: body.riskRating,
    secOfferingType: body.secOfferingType,
    propertyManager: {
      name: body.propertyManagerName,
      licenseReference: body.propertyManagerLicenseReference
    },
    trustee: {
      name: body.propertyManagerName,
      regNumber: body.propertyManagerLicenseReference
    },
    developer: { name: body.developerName, trackRecord: body.developerTrackRecord }
  };
};

const formatOfferingField = (val) => {
  const s = val == null ? "" : String(val).trim();
  return s || "—";
};

const formatSecOfferingType = (val) => {
  const key = String(val || "").trim();
  return SEC_OFFERING_TYPE_LABELS[key] || formatOfferingField(key);
};

const resolveListingAgentFields = (property) => {
  if (!property) return property;
  const pm = propertyManagerFromRecord(property);
  const agentEmail = String(
    property.agentEmail || property.listerEmail || property.submitterEmail || ""
  ).trim();
  let agentName = String(property.agentName || "").trim();
  let agentCompany = String(property.agentCompany || "").trim();
  const agentPhone = String(property.agentPhone || "").trim();

  if (!agentName && pm.name) {
    agentName = pm.name;
  }

  if (agentName && agentCompany && agentName.toLowerCase() === agentCompany.toLowerCase()) {
    agentCompany = "";
  }

  if (!agentName && agentCompany) {
    agentName = agentCompany;
    agentCompany = "";
  }

  const agentAvatarUrl = getUserAvatarUrl({ avatar: property.agentAvatarUrl }) || null;
  const agentKycApproved = property.agentKycApproved === true;

  return { ...property, agentName, agentEmail, agentCompany, agentPhone, agentAvatarUrl, agentKycApproved };
};

const hasListingAgent = (property) => {
  const resolved = resolveListingAgentFields(property);
  return !!(
    String(resolved?.agentEmail || "").trim() ||
    String(resolved?.agentName || "").trim()
  );
};

const getPropertyListerPrice = (p) => {
  const n = Number(p?.listerPrice ?? p?.price);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const getPropertyPlatformPrice = (p) => {
  const n = Number(p?.price);
  return Number.isFinite(n) && n > 0 ? n : getPropertyListerPrice(p);
};

const formatPlatformPriceNote = (listerPrice, platformPrice) => {
  const lister = Number(listerPrice) || 0;
  const platform = Number(platformPrice) || lister;
  if (!lister) return null;
  if (platform === lister) {
    return `Lister listed for ${formatUsd(lister)}. Platform listing matches (no markup yet).`;
  }
  const diff = platform - lister;
  const pct = lister > 0 ? ((diff / lister) * 100).toFixed(1) : "0";
  return `Lister listed for ${formatUsd(lister)}. CoOwn platform listing ${formatUsd(platform)} (+${formatUsd(diff)}, ${pct}% platform markup).`;
};

/** True for listings stored in Firestore (not built-in demo numeric ids). */
const isFirestoreListingId = (id) => typeof id === "string" && id.length >= 10;

/** Admin live listings: real Firestore rows first, then demos; stable title order. */
const sortAdminLiveListings = (list) =>
  [...(Array.isArray(list) ? list : [])].sort((a, b) => {
    const aLive = isFirestoreListingId(a?.id) ? 1 : 0;
    const bLive = isFirestoreListingId(b?.id) ? 1 : 0;
    if (bLive !== aLive) return bLive - aLive;
    return String(a?.title || "").localeCompare(String(b?.title || ""), undefined, { sensitivity: "base" });
  });

/** Full-site catalog for admin (not navbar country filter). */
const buildAdminLiveListingCatalog = (catalogProperties, properties) =>
  sortAdminLiveListings(
    dedupePropertiesById(catalogProperties?.length ? catalogProperties : properties)
  );

const fetchFreshPropertyListing = async (property) => {
  if (!property?.id || !isFirestoreListingId(property.id)) return property;
  try {
    const res = await apiClient.getProperty(property.id);
    const row = res?.data;
    if (!row) return property;
    if (row.visibleOnSite === false || row.isActive === false) return { ...row, visibleOnSite: false };
    return row;
  } catch (_) { /* keep cached card */ }
  return property;
};

/** Browse/detail: hide listings that are not visible on the public site (no HTTP 404). */
const propertyVisibleOnSite = (p) => p && p.visibleOnSite !== false && p.isActive !== false;

const normalizePropertyFundingFields = (property) => {
  if (!property) return property;
  if (property.primaryFundingComplete === true) {
    const shares = Number(property.shares) || 0;
    return {
      ...property,
      sharesSold: shares,
      status: 'funded',
      primaryFundingComplete: true,
      fundingProgress: shares ? 100 : property.fundingProgress
    };
  }
  const shares = Number(property.shares) || 0;
  const sharesSold = Number(property.sharesSold) || 0;
  const funded = shares > 0 && sharesSold >= shares;
  const status =
    funded
      ? 'funded'
      : String(property.status || '').toLowerCase() === 'funded' && sharesSold < shares
        ? 'funding'
        : property.status || 'funding';
  return {
    ...property,
    sharesSold: funded ? shares : sharesSold,
    status,
    primaryFundingComplete: funded || property.primaryFundingComplete === true,
    fundingProgress: shares ? ((funded ? shares : sharesSold) / shares) * 100 : property.fundingProgress
  };
};

const normalizePropertyListFunding = (list) =>
  (Array.isArray(list) ? list : []).map(normalizePropertyFundingFields);

const isPropertyFullyFunded = (property) => {
  const p = normalizePropertyFundingFields(property);
  if (!p) return false;
  if (p.primaryFundingComplete === true) return true;
  if (String(p.status || '').toLowerCase() === 'funded') return true;
  const total = Number(p.shares) || 0;
  const sold = Number(p.sharesSold) || 0;
  return total > 0 && sold >= total;
};

/** Primary offering browse — hide once all shares are sold (secondary only after that). */
const isPropertyOpenForPrimaryInvestment = (property) => {
  if (!property || getPropertyListingMode(property) !== 'sale') return false;
  if (isPropertyFullyFunded(property)) return false;
  return String(property.status || '').toLowerCase() === 'funding';
};

export {
  getPropertyListingMode,
  propertyManagerFromRecord,
  filterPropertiesByListingMode,
  mergeApiWithMockProperties,
  dedupePropertiesById,
  findPropertyById,
  getPropertyPhotoUrls,
  getPropertyCardImage,
  propertyMatchesBrowseCategory,
  propertyMatchesBrowsePriceRange,
  propertyMatchesBrowseSearch,
  getPropertyListedAt,
  formatPropertyDateTime,
  propertyMatchesListedDateFilter,
  parsePropertyTimestamp,
  isPropertyFavorited,
  isPropertyComparing,
  getUserDisplayName,
  getUserInitials,
  getUserAvatarUrl,
  normalizeFavoriteIds,
  paginateArray,
  scrollPageToTop,
  runAfterPagePaint,
  scrollToListingsAnchor,
  scrollToCoownFaq,
  resolveMarketplaceProperty,
  getMarketListingPresentation,
  marketListingIsSecondarySale,
  filterMarketListingsForView,
  getEffectiveMinInvestment,
  offeringFormFromRecord,
  buildOfferingApiBody,
  buildOfferingPropertyPatch,
  formatOfferingField,
  formatSecOfferingType,
  resolveListingAgentFields,
  hasListingAgent,
  getPropertyListerPrice,
  getPropertyPlatformPrice,
  formatPlatformPriceNote,
  isFirestoreListingId,
  sortAdminLiveListings,
  buildAdminLiveListingCatalog,
  fetchFreshPropertyListing,
  propertyVisibleOnSite,
  isPropertyFullyFunded,
  isPropertyOpenForPrimaryInvestment,
  normalizePropertyFundingFields,
  normalizePropertyListFunding,
};
